import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { DialogModule } from "primeng/dialog";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { InputTextModule } from "primeng/inputtext";
import { TooltipModule } from "primeng/tooltip";
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from "primeng/autocomplete";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DestroyRef, inject } from "@angular/core";
import { finalize, map, of, switchMap } from "rxjs";
import { ClientGroupService } from "../../../../../core/services/client-group.service";
import { ClientUserDto } from "../../../../../core/models/client-group/client-user.dto";
import { ClientGroupDetailDto } from "../../../../../core/models/client-group/client-group-detail.dto";
import { JniToastrService } from "../../../../../core/services/jni-toastr.service";

export interface GroupUser {
  name: string;
  email: string;
  selected: boolean;
  /** Identifier of an existing Nadel Portal user; undefined for a newly typed email. */
  userId?: number;
  /** Indicates the user does not yet exist as a Nadel Portal user. */
  isNew?: boolean;
  /**
   * Indicates the member's project access has been approved. `false` means the member has access
   * but receives no project or invitation email until an administrator approves the request.
   */
  isApproved?: boolean;
}

export interface AvailableGroup {
  groupId: number;
  isEnabled: boolean;
  name: string;
  description: string | null;
  userCount: number;
  selected: boolean;
  expanded: boolean;
  users: GroupUser[];
  showAddPerson: boolean;
  newPersonEmail: string;
}

@Component({
  selector: "jni-add-group-modal",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    TooltipModule,
    AutoCompleteModule,
    ProgressSpinnerModule,
    TooltipModule,
  ],
  templateUrl: "./add-group-modal.component.html",
  styleUrls: ["./add-group-modal.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddGroupModalComponent {
  @Input() visible = false;
  @Input() loading = false;
  @Input() customerAccount = "";
  @Input() projectId?: string;
  @Input() availableGroups: AvailableGroup[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() createNewGroup = new EventEmitter<void>();
  @Output() groupsAdded = new EventEmitter<AvailableGroup[]>();
  /** Emits the identifiers of approval requests created when an added member is held for approval. */
  @Output() approvalsSubmitted = new EventEmitter<number[]>();

  readonly saving = signal(false);

  /** Inline message shown beneath a group's Add Person row when a member was held for approval. */
  readonly approvalMessage = signal("");

  suggestions: ClientUserDto[] = [];
  validationMessage = "";

  private readonly _clientGroupService = inject(ClientGroupService);
  private readonly _toastr = inject(JniToastrService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _cdr = inject(ChangeDetectorRef);

  searchUsers(event: AutoCompleteCompleteEvent, group: AvailableGroup): void {
    const query = (event.query ?? "").trim();
    if (query.length < 2) {
      this.suggestions = [];
      return;
    }

    this._clientGroupService
      .searchClientUsers(this.customerAccount, query)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(users => {
        this.suggestions = users.filter(
          u =>
            !group.users.some(
              gu => gu.email.toLowerCase() === u.email.toLowerCase()
            )
        );
        this._cdr.markForCheck();
      });
  }

  onUserSelect(event: AutoCompleteSelectEvent, group: AvailableGroup): void {
    const user = event.value as ClientUserDto;
    this._addEmailToGroup(user.email, group);
  }

  addPersonToGroup(group: AvailableGroup): void {
    const raw =
      typeof group.newPersonEmail === "string"
        ? group.newPersonEmail
        : (group.newPersonEmail as ClientUserDto).email;
    this._addEmailToGroup(raw, group);
  }

  private _addEmailToGroup(raw: string, group: AvailableGroup): void {
    if (this.saving()) return;
    const email = (raw ?? "").trim();
    if (!email) {
      group.newPersonEmail = "";
      group.showAddPerson = false;
      return;
    }
    if (group.users.some(u => u.email.toLowerCase() === email.toLowerCase())) {
      group.newPersonEmail = "";
      group.showAddPerson = false;
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this._toastr.warning("Please enter a valid email address.");
      return;
    }
    this.saving.set(true);
    this.approvalMessage.set("");
    // Persist the membership via the group upsert: the new email is sent with isActive=true so the
    // backend adds it without affecting the other members. The group name and description are
    // forwarded unchanged to preserve them. When the member is added outright the group is re-fetched
    // so the newly created user's identifier is available for the subsequent project-access grant;
    // when the member is held for approval instead, no member exists yet so the fetch is skipped.
    this._clientGroupService
      .createOrUpdateGroup(this.customerAccount, {
        groupId: group.groupId,
        projectId: this.projectId,
        groupName: group.name,
        description: group.description,
        users: [{ email, isActive: true }],
      })
      .pipe(
        switchMap(result =>
          result.pendingApprovalIds?.length
            ? of({ result, detail: null })
            : this._clientGroupService
                .getGroupById(group.groupId)
                .pipe(map(detail => ({ result, detail })))
        ),
        finalize(() => {
          this.saving.set(false);
          this._cdr.markForCheck();
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: ({ result, detail }) => {
          group.newPersonEmail = "";
          group.showAddPerson = false;
          if (result.pendingApprovalIds?.length) {
            this.approvalsSubmitted.emit(result.pendingApprovalIds);
            this.approvalMessage.set(
              "User added to the approval list because their domain differs from the buyer's domain."
            );
            return;
          }
          if (!detail) return;
          this._refreshGroupUsers(group, detail);
          this.onUserSelectionChange(group);
          this._toastr.success("Success", `${email} added to ${group.name}`);
        },
        error: (err: Error) => {
          this._toastr.error(
            "Error",
            err.message || "Unable to add the person to the group."
          );
        },
      });
  }

  /**
   * Replaces a group's members with the freshly fetched detail, preserving each existing member's
   * selection state. Newly added members default to selected.
   * @param group The group whose members are refreshed.
   * @param detail The latest group detail fetched from the server.
   */
  private _refreshGroupUsers(
    group: AvailableGroup,
    detail: ClientGroupDetailDto
  ): void {
    const previousSelection = new Map(
      group.users.map(u => [u.email.toLowerCase(), u.selected])
    );

    group.users = detail.users.map(u => ({
      name: u.displayName || u.email,
      email: u.email,
      selected:
        previousSelection.get(u.email.toLowerCase()) ??
        group.showAddPerson ??
        false,
      userId: u.userId,
      isNew: false,
    }));
    group.userCount = group.users.length;
  }

  toggleGroupExpand(group: AvailableGroup): void {
    group.expanded = !group.expanded;
  }

  /**
   * Synchronises the selection state of every member to the group checkbox.
   * Selecting the group checks all members; clearing it unchecks all members.
   * @param group The group whose checkbox was toggled.
   */
  onGroupSelectionChange(group: AvailableGroup): void {
    this.validationMessage = "";
    for (const user of group.users) {
      user.selected = group.selected;
    }
  }

  /**
   * Synchronises the group checkbox to its members' selection state.
   * The group is shown as checked whenever at least one member is checked.
   * @param group The group whose member selection changed.
   */
  onUserSelectionChange(group: AvailableGroup): void {
    group.selected = group.users.some(user => user.selected);
  }

  toggleAddPerson(group: AvailableGroup): void {
    group.showAddPerson = !group.showAddPerson;
    if (!group.showAddPerson) {
      group.newPersonEmail = "";
    }
  }

  closeModal(): void {
    if (this.saving()) return;
    this.validationMessage = "";
    this.visibleChange.emit(false);
  }

  onCreateNewGroup(): void {
    if (this.saving()) return;
    this.createNewGroup.emit();
  }

  addSelectedGroups(): void {
    if (this.saving()) return;
    const selectedGroups = this.availableGroups.filter(g => g.selected);
    const groupsWithNoUsers = selectedGroups.filter(g => !this._hasUsers(g));

    if (groupsWithNoUsers.length > 0) {
      const label =
        groupsWithNoUsers.length === 1
          ? `Group "${groupsWithNoUsers[0].name}" has no users and cannot be added.`
          : "One or more selected groups have no users and cannot be added.";
      this.validationMessage = label;
      return;
    }

    this.validationMessage = "";
    this.groupsAdded.emit(selectedGroups);
  }

  private _hasUsers(group: AvailableGroup): boolean {
    return group.userCount > 0 || group.users.length > 0;
  }
}
